/**
 * Speech Recognition Error Handling
 *
 * Contains error handling logic for speech recognition failures,
 * retries, and user-friendly error messages.
 */

import { SPEECH_CONSTANTS } from '../constants';
import type {
  SpeechRecognition,
  SpeechRecognitionErrorEvent,
} from '../types/speech-recognition';

import { isBraveBrowser } from './speech-recognition';

export interface SpeechRecognitionErrorHandlers {
  setSpeechError: (error: string | null) => void;
  setIsListening: (listening: boolean) => void;
  onError?: (error: string) => void;
  retryCountRef: React.RefObject<number>;
  maxRetries: number;
  recognitionRef: React.RefObject<SpeechRecognition | null>;
}

/**
 * Handles speech recognition errors with appropriate retry logic
 */
export const handleSpeechRecognitionError = (
  errorEvent: SpeechRecognitionErrorEvent,
  handlers: SpeechRecognitionErrorHandlers
): void => {
  const {
    setSpeechError,
    setIsListening,
    onError,
    retryCountRef,
    maxRetries,
    recognitionRef,
  } = handlers;

  const errorMessage = `Speech recognition error: ${errorEvent.error}`;
  setSpeechError(errorMessage);
  onError?.(errorMessage);

  switch (errorEvent.error) {
    case 'no-speech': {
      // No action needed for no-speech errors
      break;
    }
    case 'audio-capture': {
      setIsListening(false);
      setSpeechError('Microphone access failed');
      break;
    }
    case 'not-allowed': {
      setIsListening(false);
      setSpeechError('Microphone permission denied');
      break;
    }
    case 'network': {
      const isBraveDetected = isBraveBrowser();

      if (isBraveDetected) {
        setIsListening(false);
        setSpeechError(
          'Brave browser detected: Speech recognition blocked by privacy settings. Try using Chrome or Firefox.'
        );
      } else {
        retryCountRef.current += 1;

        if (retryCountRef.current <= maxRetries) {
          setSpeechError(
            `Network error, retrying... (${retryCountRef.current}/${maxRetries})`
          );

          setTimeout(() => {
            if (recognitionRef.current) {
              try {
                recognitionRef.current.start();
              } catch {
                setIsListening(false);
                setSpeechError('Speech recognition failed. Please try again.');
              }
            }
          }, SPEECH_CONSTANTS.RETRY_DELAY);
        } else {
          setIsListening(false);
          setSpeechError(
            'Network error: Please check your internet connection and try again'
          );
        }
      }
      break;
    }
    default: {
      setIsListening(false);
      break;
    }
  }
};

/**
 * Handles general speech recognition startup errors
 */
export const handleSpeechRecognitionStartupError = (
  error: unknown,
  handlers: Pick<
    SpeechRecognitionErrorHandlers,
    'setSpeechError' | 'onError' | 'recognitionRef'
  >
): void => {
  const { setSpeechError, onError, recognitionRef } = handlers;

  const err = error as { name?: string };

  if (err.name === 'InvalidStateError' && recognitionRef.current) {
    try {
      recognitionRef.current.stop();
    } catch {
      /* ignore */
    }

    setTimeout(() => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.start();
        } catch {
          setSpeechError('Failed to restart speech recognition');
          onError?.('Failed to restart speech recognition');
        }
      }
    }, 100);
  } else {
    setSpeechError('Failed to start speech recognition');
    onError?.('Failed to start speech recognition');
  }
};

/**
 * Gets user-friendly error messages for different error scenarios
 */
export const getErrorMessage = (scenario: string): string => {
  switch (scenario) {
    case 'no-connection': {
      return 'No internet connection. Please check your network and try again.';
    }
    case 'permission-denied': {
      return 'Microphone permission denied';
    }
    case 'not-supported': {
      return 'Speech recognition not supported in this browser';
    }
    case 'brave-timeout': {
      return 'Speech recognition timed out. Brave browser may block speech recognition due to privacy settings.';
    }
    case 'general-timeout': {
      return 'Speech recognition timed out. Please try again.';
    }
    default: {
      return 'An unexpected error occurred. Please try again.';
    }
  }
};
